import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Home, KeyRound, ArrowRight, ArrowLeft, MapPin, User, AlertCircle, Building2, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("login"); // login | session
  const [inviteCode, setInviteCode] = useState("");
  const [partnerToken, setPartnerToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Fetch addresses when postcode changes
  useEffect(() => {
    const fetchAddresses = async () => {
      if (postcode.length >= 5) {
        setLoadingAddresses(true);
        try {
          const res = await axios.get(`${API}/addresses/${postcode.replace(/\s/g, '')}`);
          setAddresses(res.data.addresses || []);
          setSelectedAddress(null);
        } catch (e) {
          setAddresses([]);
        } finally {
          setLoadingAddresses(false);
        }
      } else {
        setAddresses([]);
        setSelectedAddress(null);
      }
    };
    
    const debounce = setTimeout(fetchAddresses, 500);
    return () => clearTimeout(debounce);
  }, [postcode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      toast.error("Please enter your invite code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/partner/login`, {
        invite_code: inviteCode.trim()
      });
      
      if (response.data.success) {
        setPartnerToken(response.data.partner_token);
        setStep("session");
        toast.success("Welcome, Partner");
      } else {
        toast.error(response.data.message || "Invalid invite code");
      }
    } catch (error) {
      toast.error("Invalid invite code");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      toast.error("Please enter your client's first name");
      return;
    }
    
    if (!postcode.trim() || postcode.length < 5) {
      toast.error("Please enter a valid UK postcode");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/partner/session`, {
        first_name: firstName.trim(),
        postcode: postcode.trim(),
        partner_token: partnerToken,
        selected_address: selectedAddress
      });
      
      // Mark this session as a partner session (skips email capture)
      sessionStorage.setItem(`cipher_partner_${response.data.session_id}`, "true");
      sessionStorage.setItem(`partner_session_${response.data.session_id}`, JSON.stringify({
        first_name: firstName.trim(),
        postcode: postcode.trim().toUpperCase().replace(/\s+/g, ' '),
        email: "partner@cipher.app",
      }));
      
      navigate(`/partner/express/${response.data.session_id}`);
    } catch (error) {
      toast.error("Failed to start session. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Header */}
      <header className="cipher-container pt-8 pb-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2"
            data-testid="back-to-home"
          >
            <div className="w-8 h-8 rounded-lg bg-cipher-green flex items-center justify-center">
              <Home className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className="font-heading font-bold text-lg text-white">HiPPO</span>
          </button>
          <span className="px-3 py-1.5 rounded-full bg-cipher-green/20 text-cipher-green text-xs font-medium border border-cipher-green/30">
            Partner Portal
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col justify-center cipher-container pb-12">
        {step === "login" ? (
          <div className="opacity-0 animate-fade-up">
            <Card className="bg-white/5 border border-white/10 rounded-2xl p-6">
              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-cipher-green/10 flex items-center justify-center mx-auto mb-6">
                <KeyRound className="w-7 h-7 text-cipher-green" strokeWidth={1.5} />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-heading font-bold text-white text-center mb-2">
                Partner Access
              </h1>
              <p className="text-white/50 text-center mb-8 text-sm">
                Enter your invite code to continue
              </p>

              {/* Form */}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="inviteCode" className="cipher-label">
                    Invite Code
                  </label>
                  <Input
                    id="inviteCode"
                    type="text"
                    placeholder="Enter your code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="cipher-input uppercase tracking-wider text-center font-mono"
                    autoComplete="off"
                    data-testid="invite-code-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="cipher-btn-primary w-full"
                  disabled={loading}
                  data-testid="partner-login-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Continue
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </Card>

            {/* Back Link */}
            <button 
              onClick={() => navigate('/')}
              className="cipher-btn-ghost mx-auto mt-6 text-sm flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </button>
          </div>
        ) : (
          <div className="opacity-0 animate-fade-up">
            <Card className="bg-white/5 border border-white/10 rounded-2xl p-6">
              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-cipher-green/10 flex items-center justify-center mx-auto mb-6">
                <User className="w-7 h-7 text-cipher-green" strokeWidth={1.5} />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-heading font-bold text-white text-center mb-2">
                New Assessment
              </h1>
              <p className="text-white/50 text-center mb-8 text-sm">
                Enter client details to begin
              </p>

              {/* Form */}
              <form onSubmit={handleStartSession} className="space-y-4">
                <div>
                  <label htmlFor="firstName" className="cipher-label">
                    Client's First Name
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="cipher-input"
                    autoComplete="given-name"
                    data-testid="partner-first-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="postcode" className="cipher-label">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    Property Postcode
                  </label>
                  <div className="relative">
                    <Input
                      id="postcode"
                      type="text"
                      placeholder="e.g. SW1A 1AA"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                      className="cipher-input uppercase"
                      autoComplete="postal-code"
                      maxLength={8}
                      data-testid="partner-postcode-input"
                    />
                    {loadingAddresses && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Search className="w-4 h-4 text-white/40 animate-pulse" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Address Dropdown */}
                {addresses.length > 0 && (
                  <div>
                    <label className="cipher-label">
                      <Building2 className="w-3 h-3 inline mr-1" />
                      Select Property
                    </label>
                    <Select value={selectedAddress?.address_line || ""} onValueChange={(value) => {
                      const addr = addresses.find(a => a.address_line === value);
                      setSelectedAddress(addr);
                    }}>
                      <SelectTrigger className="cipher-input" data-testid="address-select">
                        <SelectValue placeholder="Select specific property..." />
                      </SelectTrigger>
                      <SelectContent>
                        {addresses.map((addr, i) => (
                          <SelectItem key={i} value={addr.address_line}>
                            {addr.address_line}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/50 mt-1">
                      {addresses.length} properties found at {postcode}
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="cipher-btn-primary w-full"
                  disabled={loading}
                  data-testid="partner-start-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Starting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Start Assessment
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </Card>

            {/* Partner Note */}
            <div className="mt-6 p-4 bg-cipher-green/5 rounded-2xl border border-cipher-green/10">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-cipher-green flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white/60">
                  <span className="font-medium text-white">Partner benefit:</span> Full insight access without payment for your clients.
                </p>
              </div>
            </div>

            {/* Back Link */}
            <button 
              onClick={() => setStep("login")}
              className="cipher-btn-ghost mx-auto mt-4 text-sm flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Change partner code
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="cipher-container py-6">
        <p className="text-center text-xs text-white/40">
          HiPPO by StudioNPG — Partner Foundation
        </p>
      </footer>
    </div>
  );
}
